import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCopy, FiMapPin } from 'react-icons/fi';
import SEO from '../components/SEO';
import type { Location, Event } from '../types/trip';

type TripDetails = {
  id: string;
  title: string;
  description?: string | null;
  cover_image_url?: string | null;
  start_date?: number | null;
  end_date?: number | null;
  status?: string | null;
  privacy?: string | null;
  created_by?: string | null;
  is_template?: boolean | null;
  verified?: boolean | null;
  type?: string | null;
  created_at?: number | null;
  updated_at?: number | null;
};

type ApiResponse = {
  success: boolean;
  trip: TripDetails;
  locations?: Location[];
  events?: Event[];
};

export default function TripDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<TripDetails | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTrip = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log('🟢 [TripDetail] Fetching trip:', id);
        
        const response = await fetch(`/api/trips/${id}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Trip not found');
          }
          throw new Error('Failed to fetch trip');
        }
        
        const data: ApiResponse = await response.json();
        
        if (data.success && data.trip) {
          setTrip(data.trip);
          setLocations(data.locations ?? []);
          setEvents(data.events ?? []);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err: any) {
        console.error('🔴 [TripDetail] Error:', err);
        setError(err.message || 'Failed to load trip');
      } finally {
        setLoading(false);
      }
    };
    
    loadTrip();
  }, [id]);

  const formatDate = (timestamp: number | null | undefined) => {
    if (!timestamp) return null;
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const copyCoordinates = (latitude: number, longitude: number) => {
    const coordinates = `${latitude}, ${longitude}`;
    navigator.clipboard.writeText(coordinates);
    alert('Coordinates copied to clipboard!');
  };

  const openGoogleMaps = (latitude: number, longitude: number, name?: string | null) => {
    const url = `https://www.google.com/maps/search/${name ? encodeURIComponent(name) : ''}/@${latitude},${longitude},15z`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="flex-1 bg-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-96 bg-gray-200 mb-8" />
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 w-2/3" />
              <div className="h-4 bg-gray-200 w-1/2" />
              <div className="h-4 bg-gray-200 w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="flex-1 bg-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl font-bold text-black mb-4">Trip Not Found</h1>
          <p className="text-gray-600 mb-8">{error || 'The trip you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/recommended/trips')}
            className="bg-black text-waybook-green px-6 py-3 font-semibold hover:bg-gray-900 transition-colors"
          >
            Back to Trips
          </button>
        </div>
      </div>
    );
  }

  const seoTitle = `${trip.title} - Travel Trip | Waybook`;
  const seoDescription = trip.description 
    ? `${trip.description.substring(0, 155)}...`
    : `Explore ${trip.title}. ${locations.length > 0 ? `Visit ${locations.length} amazing location${locations.length > 1 ? 's' : ''}.` : 'Discover this exciting travel experience.'}`;

  const eventDates = events
    .filter(e => e.start_date)
    .map(e => new Date(e.start_date! * 1000));

  return (
    <div className="flex-1 bg-white py-12">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={`travel trip, ${trip.title}, ${locations.map(l => l.country).filter(Boolean).join(', ')}, itinerary, travel plan`}
        ogImage={trip.cover_image_url || '/og-image.png'}
        canonicalUrl={`https://www.waybook.me/trip/${id}`}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'TouristTrip',
          name: trip.title,
          description: trip.description || `Explore ${trip.title}`,
          ...(trip.cover_image_url && { image: trip.cover_image_url }),
          ...(trip.start_date && { startDate: new Date(trip.start_date * 1000).toISOString() }),
          ...(trip.end_date && { endDate: new Date(trip.end_date * 1000).toISOString() }),
          ...(locations.length > 0 && {
            itinerary: {
              '@type': 'ItemList',
              itemListElement: locations.map((location, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                item: {
                  '@type': 'Place',
                  name: location.name,
                  ...(location.description && { description: location.description }),
                  ...(location.address && { address: location.address }),
                  ...(location.latitude && location.longitude && {
                    geo: {
                      '@type': 'GeoCoordinates',
                      latitude: location.latitude,
                      longitude: location.longitude
                    }
                  })
                }
              }))
            }
          })
        }}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/recommended/trips')}
          className="mb-6 text-sm text-gray-600 hover:text-black flex items-center gap-2"
        >
          ← Back to Trips
        </button>

        {/* Hero Image */}
        {trip.cover_image_url && (
          <div className="mb-8 overflow-hidden bg-gray-100 border border-gray-200">
            <img
              src={trip.cover_image_url}
              alt={trip.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Trip Header */}
        <div className="mb-8 pb-8 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <h1 className="text-4xl font-bold text-black">{trip.title}</h1>
          </div>
        </div>

        {/* Description */}
        {trip.description && (
          <div className=" mb-8">
            <p className="text-gray-700 whitespace-pre-wrap">{trip.description}</p>
          </div>
        )}

        {/* Events */}
        {events.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-black mb-4">Events ({events.length})</h3>
            <div className="space-y-4">
              {events.map((event) => (
                <div key={event.id} className="border border-gray-200 p-4 flex gap-4">
                  {/* Image on left */}
                  {event.cover_image_url && (
                    <div className="flex-shrink-0 w-48 h-48 overflow-hidden bg-gray-100">
                      <img
                        src={event.cover_image_url}
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Text content on right */}
                  <div className="flex-1">
                    <h4 className="font-semibold text-base text-black">{event.title}</h4>

                    
                    {(event.location_name || event.location_city || event.location_country) && (
                      <div className="flex items-center gap-2 mt-2 flex-row">
                        {event.latitude && event.longitude && (
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => copyCoordinates(event.latitude!, event.longitude!)}
                              title="Copy coordinates"
                              className="text-gray-500 hover:text-black transition-colors"
                            >
                              <FiCopy size={16} />
                            </button>
                            <button
                              onClick={() => openGoogleMaps(event.latitude!, event.longitude!, event.location_name)}
                              title="Open in Google Maps"
                              className="text-gray-500 hover:text-black transition-colors"
                            >
                              <FiMapPin size={16} />
                            </button>
                          </div>
                        )}
                        <p className="text-sm text-gray-600 ml-1">
                          {[event.location_name, event.location_city, event.location_country].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    )}
                    
                    {event.description && (
                      <p className="text-sm text-gray-700 mt-3">{event.description}</p>
                    )}
                    
                    {event.tags && (() => {
                      try {
                        const tagsArray = typeof event.tags === 'string' ? JSON.parse(event.tags) : event.tags;
                        const tags = Array.isArray(tagsArray) ? tagsArray : [];
                        return tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {tags.map((tag: string, idx: number) => (
                              <span key={idx} className="bg-gray-100 px-2 py-1 text-xs rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                        );
                      } catch {
                        return null;
                      }
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
